import React from "react";
import styled from "styled-components";
import { Link } from "react-scroll";
import { Link as RouterLink } from "react-router-dom";
// Icons
import { FaRocket, FaLinkedin, FaFacebook, FaInstagram, FaTwitter } from "react-icons/fa";
// Assets
import LogoIcon from "../../assets/optimized/logo.webp";


export default function Footer() {
  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  return (
    <Wrapper>
      <FooterBackground>
        <div className="container">
          <FooterContent>
            <FooterSection>
              <Link className="flexCenter animate pointer" to="home" smooth={true} offset={-80}>
                <FooterLogo src={LogoIcon} height={180} width={350} alt="Anton Tech Logo"/>
              </Link>
              <CompanyDescription className="whiteColor font14">
                Award-winning African tech company providing enterprise-grade intelligence solutions through four core pillars: ESG & Sustainability, Precision Agriculture, Compliance & Verification, and Business Intelligence.
              </CompanyDescription>
            </FooterSection>
            
            <FooterSection>
              <FooterTitle className="whiteColor font18 extraBold">Quick Links</FooterTitle>
              <FooterLinks >
                <FooterLink to="home" smooth={true} offset={-80}>Home</FooterLink>
                <FooterLink to="about" smooth={true} offset={-80}>About</FooterLink>
                <FooterLink to="services" smooth={true} offset={-80}>Services</FooterLink>
                <LaunchEventFooterText to="/developer-tools">
                  Developer Tools
                </LaunchEventFooterText>
                <FooterLink to="field-demo" smooth={true} offset={-80}>Field Demo</FooterLink>
                <FooterLink to="impact" smooth={true} offset={-80}>Impact</FooterLink>
                <LaunchEventFooterText to="/launch-event">
                  <FaRocket style={{ marginRight: '8px', fontSize: '14px' }} />
                  Anton Tech Official Launch Event
                </LaunchEventFooterText>
                <LaunchEventFooterText to="/who-is-anton-for">
                  Who is Anton for?
                </LaunchEventFooterText>
                <FooterLink to="contact" smooth={true} offset={-80}>Contact</FooterLink>
              </FooterLinks>
            </FooterSection>
            
            <FooterSection>
              <FooterTitle className="whiteColor font18 extraBold">Platform Access</FooterTitle>
              <PlatformLinks>
                <PlatformLink href="https://app.anton.co.bw/login" target="_blank" rel="noopener noreferrer">
                  Access Project Anton
                </PlatformLink>
              </PlatformLinks>
            </FooterSection>
          </FooterContent>
          
          <FooterBottom>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <SocialMediaSection>
                <SocialMediaLink href="https://www.linkedin.com/company/anton-tech" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin />
                </SocialMediaLink>
                <SocialMediaLink href="https://www.facebook.com/antontechbw" target="_blank" rel="noopener noreferrer">
                  <FaFacebook />
                </SocialMediaLink>
                <SocialMediaLink href="https://www.instagram.com/antontechbw/" target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </SocialMediaLink>
                <SocialMediaLink href="https://x.com/AntonTechBW" target="_blank" rel="noopener noreferrer">
                  <FaTwitter />
                </SocialMediaLink>
              </SocialMediaSection>
              <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
                <LaunchEventFooterText 
                  to="/terms-and-conditions" 
                  style={{ 
                    fontSize: '13px', 
                    color: 'white',
                    '&:hover': { color: '#079487' }
                  }}
                >
                  Terms & Conditions
                </LaunchEventFooterText>
                <span style={{ color: '#666' }}>|</span>
                <LaunchEventFooterText 
                  to="/privacy-policy" 
                  style={{ 
                    fontSize: '13px', 
                    color: 'white',
                    '&:hover': { color: '#079487' }
                  }}
                >
                  Privacy Policy
                </LaunchEventFooterText>
              </div>
              <Copyright className="whiteColor font13">
                © {getCurrentYear()} - <span className="orangeColor font13">Anton</span> <span className="blueColor font13">Tech</span>. All Rights Reserved
              </Copyright>
            </div>
            <BackToTop to="home" smooth={true} offset={-80}>
              Back to top ↑
            </BackToTop>
          </FooterBottom>
        </div>
      </FooterBackground>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
`;

const FooterBackground = styled.div`
  background: linear-gradient(135deg, 
    rgba(7, 148, 135, 0.15) 0%, 
    rgba(27, 94, 32, 0.1) 25%,
    rgba(7, 148, 135, 0.1) 75%,
    rgba(25, 118, 210, 0.15) 100%
  );
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 15% 85%, rgba(7, 148, 135, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 85% 15%, rgba(7, 148, 135, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 50% 50%, rgba(25, 118, 210, 0.1) 0%, transparent 60%),
      radial-gradient(circle at 30% 30%, rgba(102, 187, 106, 0.1) 0%, transparent 50%);
    animation: cosmicShift 12s ease-in-out infinite;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(30deg, rgba(7, 148, 135, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(7, 148, 135, 0.05) 87.5%, rgba(7, 148, 135, 0.05)),
      linear-gradient(150deg, rgba(7, 148, 135, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(7, 148, 135, 0.05) 87.5%, rgba(7, 148, 135, 0.05)),
      linear-gradient(30deg, rgba(25, 118, 210, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(25, 118, 210, 0.05) 87.5%, rgba(25, 118, 210, 0.05)),
      linear-gradient(150deg, rgba(102, 187, 106, 0.05) 12%, transparent 12.5%, transparent 87%, rgba(102, 187, 106, 0.05) 87.5%, rgba(102, 187, 106, 0.05));
    background-size: 80px 80px, 80px 80px, 40px 40px, 40px 40px;
    background-position: 0 0, 0 0, 40px 40px, 40px 40px;
    animation: futuristicGrid 25s linear infinite;
    z-index: 1;
  }
  
  /* Ensure content is above background effects */
  & > * {
    position: relative;
    z-index: 2;
  }
  
  @keyframes cosmicShift {
    0%, 100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
    33% {
      transform: scale(1.1) rotate(2deg);
      opacity: 0.8;
    }
    66% {
      transform: scale(0.9) rotate(-1deg);
      opacity: 0.9;
    }
  }
  
  @keyframes futuristicGrid {
    0% {
      background-position: 0 0, 0 0, 40px 40px, 40px 40px;
    }
    100% {
      background-position: 80px 0, 80px 0, 120px 40px, 120px 40px;
    }
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  padding: 60px 0 40px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.img`
  margin-bottom: 20px;
`;

const CompanyDescription = styled.p`
  line-height: 1.6;
  opacity: 0.9;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const FooterTitle = styled.h4`
  margin-bottom: 20px;
  color: #079487;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled(Link)`
  color: white;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: #079487;
  }
`;

const PlatformLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const Copyright = styled.p`
  opacity: 0.8;
`;

const LaunchEventFooterText = styled(RouterLink)`
  color: white;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  position: relative;
  
  &:hover {
    color: #079487;
    transform: scale(1.05);
  }
`;

const PlatformLink = styled.a`
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  padding: 12px 25px;
  color: white;
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.2) 0%, rgba(7, 148, 135, 0.1) 100%);
  text-decoration: none;
  font-weight: 700;
  font-size: 15px;
  transition: all 0.3s ease;
  display: block;
  text-align: center;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    background: linear-gradient(135deg, #079487 0%, #0a7c73 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(7, 148, 135, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const BackToTop = styled(Link)`
  color: white;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: #079487;
  }
`;

const SocialMediaSection = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const SocialMediaLink = styled.a`
  color: white;
  font-size: 18px;
  transition: all 0.3s ease;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    color: #079487;
    transform: translateY(-2px);
  }
  
  &:nth-child(1):hover {
    color: #0077B5; /* LinkedIn blue */
  }
  
  &:nth-child(2):hover {
    color: #1877F2; /* Facebook blue */
  }
  
  &:nth-child(3):hover {
    color: #E4405F; /* Instagram pink */
  }
  
  &:nth-child(4):hover {
    color: #1DA1F2; /* Twitter blue */
  }
`;